import React, { useEffect, useRef, useState } from 'react'
import './Redesign.css'
import { FaDiscord, FaHandHoldingHeart, FaArrowRight, FaChevronDown, FaExternalLinkAlt } from 'react-icons/fa'
import bearHug from '../assets/Emoji_Plushie_Bear_Hug.webp'
import {
  REGISTER_URL, DISCORD_URL, CROWDFUNDING_URL, KOFI_URL, RULES_URL, PAST_SEASONS,
  PRIZE_POOL, PRIZE_POOL_BASE, PRIZE_POOL_TOTAL, FUNDRAISED, FUNDRAISED_AT, formatUSD, PRIZE_SPLIT, FORMAT, SERVER, SCHEDULE_RANGE,
  SCHEDULE_NOTE, REGISTRATION_NOTE, CALENDAR_DAYS, WEEKDAYS, PLAY_DAYS, MAPS,
} from '../data/season3'
import { ART } from '../data/art'
import Bracket from './Bracket'

const WEEKENDS = [
  { label: 'WEEKEND 01', days: PLAY_DAYS.slice(0, 2) },
  { label: 'WEEKEND 02', days: PLAY_DAYS.slice(2) },
]

const PLAY_DAY_NUMBERS = new Set(PLAY_DAYS.map(({ day }) => day))

const localTime = (iso) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(new Date(iso))

const pad = (n) => String(n).padStart(2, '0')

// Counts down to the next published play-day start time; hidden once all have begun.
const useNextPlayDay = () => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const next = PLAY_DAYS.find(({ start }) => new Date(start).getTime() > now)
  if (!next) return null
  const ms = new Date(next.start).getTime() - now
  return {
    playDay: next,
    isFirst: next === PLAY_DAYS[0],
    parts: [
      ['DAYS', Math.floor(ms / 86400000)],
      ['HRS', Math.floor(ms / 3600000) % 24],
      ['MIN', Math.floor(ms / 60000) % 60],
      ['SEC', Math.floor(ms / 1000) % 60],
    ],
  }
}

const Countdown = () => {
  const next = useNextPlayDay()
  if (!next) return null
  return (
    <div className="nx-countdown" aria-live="off">
      <p className="nx-kicker">
        {next.isFirst ? 'FIRST MATCH DAY IN' : `NEXT PLAY DAY · OCT ${next.playDay.day} · IN`}
      </p>
      <div className="nx-countdown-units">
        {next.parts.map(([label, value]) => (
          <div className="nx-unit" key={label}>
            <span className="nx-unit-value">{pad(value)}</span>
            <span className="nx-unit-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

const MapPool = () => {
  const [active, setActive] = useState(0)
  const map = MAPS[active]
  return (
    <div className="nx-maps">
      <div className="nx-map-stage">
        {MAPS.map((m, i) => (
          <img
            key={m.name}
            src={m.img}
            alt={i === active ? m.name : ''}
            className={i === active ? 'is-active' : ''}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}
        <div className="nx-map-caption">
          <span className="nx-map-index">{pad(active + 1)} / {MAPS.length}</span>
          <span className="nx-map-title">{map.name}</span>
        </div>
      </div>
      <ol className="nx-map-list">
        {MAPS.map((m, i) => (
          <li key={m.name}>
            <button
              type="button"
              className={i === active ? 'is-active' : ''}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
            >
              <span className="nx-map-num">{pad(i + 1)}</span>
              <img src={m.img} alt="" loading="lazy" />
              <span className="nx-map-name">{m.name}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

// Dropdown of previous seasons; closes on outside click or Escape.
const PastSeasons = () => {
  const ref = useRef(null)
  useEffect(() => {
    const close = (e) => {
      if (!ref.current?.open) return
      if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current.contains(e.target)) {
        ref.current.open = false
      }
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', close)
    }
  }, [])
  return (
    <details className="nx-past" ref={ref}>
      <summary>
        Past Seasons <FaChevronDown />
      </summary>
      <ul className="nx-past-menu">
        {PAST_SEASONS.map(({ label, href }) => (
          <li key={label}>
            <a href={href} target="_blank" rel="noopener noreferrer">
              {label} <FaExternalLinkAlt />
            </a>
          </li>
        ))}
      </ul>
    </details>
  )
}

const RegisterLink = ({ className = '' }) => (
  <a className={`nx-btn nx-btn-primary ${className}`} href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
    REGISTER TO COMPETE <FaArrowRight />
  </a>
)

const ArtImage = ({ art, className }) => (
  <img className={className} src={art.src} alt="" style={{ objectPosition: art.position }} />
)

const Redesign = () => (
  <div className="nx-site">
    <header className="nx-nav">
      <a href="#top" className="nx-wordmark">
        HCC<span>S3</span>
      </a>
      <nav className="nx-nav-links">
        <a href="#overview">Overview</a>
        <a href="#schedule">Schedule</a>
        <a href="#bracket">Bracket</a>
        <a href="#maps">Maps</a>
      </nav>
      <div className="nx-nav-actions">
        <a className="nx-nav-text" href={RULES_URL} target="_blank" rel="noopener noreferrer">
          Rules
        </a>
        <PastSeasons />
        <a className="nx-icon" href={DISCORD_URL} target="_blank" rel="noopener noreferrer" aria-label="Join Discord" title="Discord">
          <FaDiscord />
        </a>
        <a className="nx-icon" href={CROWDFUNDING_URL} target="_blank" rel="noopener noreferrer" aria-label="Contribute to the prize pool" title="Crowdfunding">
          <FaHandHoldingHeart />
        </a>
        <a className="nx-btn nx-btn-primary nx-btn-sm" href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
          REGISTER
        </a>
      </div>
    </header>

    <section className="nx-hero" id="top">
      <div className="nx-hero-art" aria-hidden="true">
        <ArtImage art={ART.hero} />
      </div>
      <div className="nx-hero-shade" aria-hidden="true" />
      <div className="nx-wrap nx-hero-inner">
        <p className="nx-kicker">HEROES OF THE STORM · SEASON 3 · OCTOBER 2026</p>
        <h1 className="nx-hero-title">
          <span>HEROES</span>
          <span className="nx-glow">CAPTAINS</span>
          <span>CLASH</span>
        </h1>
        <div className="nx-hero-actions">
          <RegisterLink />
          <a className="nx-btn nx-btn-ghost" href={DISCORD_URL} target="_blank" rel="noopener noreferrer">
            <FaDiscord /> JOIN DISCORD
          </a>
        </div>
        <Countdown />
      </div>
      <span className="nx-hero-s3" aria-hidden="true">S3</span>
    </section>


    <section className="nx-section" id="overview">
      <div className="nx-wrap">
        <div className="nx-stats">
          <div className="nx-stat nx-stat-prize">
            <ArtImage art={ART.overview} className="nx-stat-art" />
            <p className="nx-kicker">PRIZE POOL</p>
            <p className="nx-stat-value nx-glow">{PRIZE_POOL}</p>
            <p className="nx-prize-breakdown">
              {formatUSD(PRIZE_POOL_BASE)} base + <strong>{formatUSD(FUNDRAISED)} raised</strong> on GoFundMe
              {FUNDRAISED_AT && <span> · updated {new Date(FUNDRAISED_AT).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
            </p>
            <div className="nx-podium">
              {PRIZE_SPLIT.map(({ place, percent }) => (
                <div className="nx-podium-row" key={place}>
                  <span className="nx-podium-place">{place}</span>
                  <span className="nx-podium-bar">
                    <span style={{ width: `${(percent / PRIZE_SPLIT[0].percent) * 100}%` }} />
                  </span>
                  <span className="nx-podium-pct">{percent}%</span>
                  <span className="nx-podium-amt">{formatUSD((PRIZE_POOL_TOTAL * percent) / 100)}</span>
                </div>
              ))}
            </div>
            <a className="nx-text-link" href={CROWDFUNDING_URL} target="_blank" rel="noopener noreferrer">
              <FaHandHoldingHeart /> Contribute to the prize pool
            </a>
          </div>
          <div className="nx-stat">
            <p className="nx-kicker">FORMAT</p>
            <p className="nx-stat-value">{FORMAT}</p>
          </div>
          <div className="nx-stat">
            <p className="nx-kicker">SERVER</p>
            <p className="nx-stat-value">{SERVER}</p>
          </div>
        </div>
      </div>
    </section>

    <div className="nx-art-banner" aria-hidden="true">
      <ArtImage art={ART.banner} />
    </div>

    <section className="nx-section" id="schedule">
      <div className="nx-wrap">
        <div className="nx-section-head">
          <h2 className="nx-h2">SCHEDULE</h2>
          <p className="nx-sub">{SCHEDULE_RANGE}</p>
        </div>

        <div className="nx-weekends">
          {WEEKENDS.map(({ label, days }, i) => (
            <div className="nx-weekend" key={label}>
              <ArtImage art={ART.weekends[i]} className="nx-weekend-art" />
              <p className="nx-kicker">{label}</p>
              {days.map(({ day, times, start }) => (
                <div className="nx-playday" key={day}>
                  <div className="nx-playday-date">
                    <span className="nx-playday-month">OCT</span>
                    <span className="nx-playday-num">{day}</span>
                  </div>
                  <div className="nx-playday-info">
                    <span className="nx-playday-label">PLAY DAY</span>
                    <span className="nx-playday-times">{times.join(' · ')}</span>
                    <span className="nx-playday-local">Your time: {localTime(start)}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="nx-strip" role="table" aria-label={SCHEDULE_RANGE}>
          <div className="nx-strip-row" role="row">
            {WEEKDAYS.map((d) => (
              <span className="nx-strip-head" role="columnheader" key={d}>{d}</span>
            ))}
          </div>
          {[CALENDAR_DAYS.slice(0, 7), CALENDAR_DAYS.slice(7)].map((week, w) => (
            <div className="nx-strip-row" role="row" key={w}>
              {week.map((day) => (
                <span
                  role="cell"
                  key={day}
                  className={`nx-strip-day${PLAY_DAY_NUMBERS.has(day) ? ' is-play' : ''}`}
                >
                  {day}
                </span>
              ))}
            </div>
          ))}
        </div>
        <p className="nx-note">{SCHEDULE_NOTE}</p>
      </div>
    </section>

    <section className="nx-section" id="bracket">
      <div className="nx-wrap">
        <div className="nx-section-head">
          <h2 className="nx-h2">BRACKET &amp; SCHEDULE</h2>
        </div>
        <Bracket />
      </div>
    </section>

    <section className="nx-section" id="maps">
      <div className="nx-wrap">
        <div className="nx-section-head">
          <h2 className="nx-h2">MAP POOL</h2>
        </div>
        <MapPool />
      </div>
    </section>

    <section className="nx-section nx-final">
      <div className="nx-wrap nx-final-inner">
        <ArtImage art={ART.cta} className="nx-final-art" />
        <h2 className="nx-h2">REGISTRATION</h2>
        <p className="nx-sub">{REGISTRATION_NOTE}</p>
        <div className="nx-hero-actions">
          <RegisterLink />
          <a className="nx-btn nx-btn-ghost" href={CROWDFUNDING_URL} target="_blank" rel="noopener noreferrer">
            <FaHandHoldingHeart /> CONTRIBUTE
          </a>
        </div>
      </div>
    </section>

    <footer className="nx-footer">
      <div className="nx-wrap nx-footer-inner">
        <p className="nx-kicker">HEROES CAPTAINS CLASH / SEASON 3 / 2026</p>
        <p className="nx-credit">
          Made with <img src={bearHug} alt="Bear Hug" /> by Nintorii · Support me on{' '}
          <a href={KOFI_URL} target="_blank" rel="noopener noreferrer">Ko-Fi</a>!
        </p>
      </div>
    </footer>
  </div>
)

export default Redesign
